"use client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { BsArrowUpRight } from "react-icons/bs";
import { HiViewGrid, HiViewList } from "react-icons/hi";
import { PiCaretLeftBold, PiCaretRightBold } from "react-icons/pi";
import { useTranslations } from "next-intl";

type ProjectStatus = "Live" | "Building" | "Coming Soon";

interface Project {
  num: string;
  category: string;
  title: string;
  description: string;
  stack: { name: string }[];
  image: string;
  darkImage?: string;
  live?: string;
  github?: string;
  status: ProjectStatus;
}

const projects: Project[] = [
  {
    num: "01",
    category: "frontend",
    title: "NJCapital",
    description:
      "Digital lending based on data sciences, has caused a fundamental transformation in the experience of the retail borrower. NJ Capital Private Limited (NJC) marks the entry of NJ group into digital lending.",
    stack: [
      { name: "React Js" },
      { name: "Redux Toolkit" },
      { name: "Bootstrap" },
    ],
    image: "/assets/workImages/ifa-ss-1.png",
    live: "https://www.njcapital.in/",
    status: "Live",
  },
  {
    num: "02",
    category: "frontend",
    title: "MT-Hood E-Shopping Website",
    description:
      "Mount Hood Meadows is one of the largest ski resorts in Oregon and the largest ski resort on Mount Hood. It is located about 67 miles east of Portland, and 35 miles from Hood River along Oregon Route 35. It has both Alpine and Nordic ski areas and offers night skiing, lessons and equipment rentals. In the summer Meadows offers scenic chair rides, a premier hiking experience on Mt. Hood and events on the weekends.",
    stack: [
      { name: "Next.js" },
      { name: "Tailwind.css" },
      { name: "Strapi" },
      { name: "Scss" },
    ],
    image: "/assets/workImages/ss-2.png",
    live: "https://www.skihood.com/",
    status: "Live",
  },
  {
    num: "03",
    category: "frontend",
    title: "Tradeics",
    description:
      "Tradeics Inc. a Saudi Arabia and USA registered company that empowers B2B companies worldwide At Tradeics, we’re redefining the way businesses manage their procurement and sales. We offer an innovative suite of solutions powered by cutting-edge technologies like blockchain, AI, and cloud computing. From Procure-to-Pay Automation and B2B Sales Management to a B2B E-commerce Marketplace, we are a one-stop platform designed to help businesses grow faster, operate more efficiently, and streamline their processes across all areas of procurement, sales, and operations.",
    stack: [
      { name: "Next.js" },
      { name: "Tailwind.css" },
      { name: "ShadCn" },
      { name: "Stripe" },
      { name: "Strapi" },
      { name: "Scss" },
    ],
    image: "/assets/workImages/ss-3.png",
    live: "https://www.tradeics.com/",
    status: "Live",
  },
  {
    num: "04",
    category: "frontend",
    title: "BetterDiagnostics.ai",
    description:
      "AI-powered dental diagnostics platform cleared by US FDA, Canada Health, and MHRA UK. Founded by Dr. Rishima Goyal and Rajeev Nehra, it combines clinical dental expertise with 20+ years of Fortune 500 tech experience. Features AI X-ray diagnostics with color-coded outputs, an AI Receptionist for automated call handling and appointment booking, hands-free Voice Perio Charting, and Voice Notes for clinical documentation — all integrating seamlessly with existing Dental PMS and imaging software.",
    stack: [
      { name: "Next.js" },
      { name: "Tailwind CSS" },
      { name: "AI / ML" },
      { name: "HIPAA Compliant" },
    ],
    image: "/assets/workImages/betterdiagnostics.png",
    live: "https://betterdiagnostics.ai/",
    status: "Live",
  },
  {
    num: "05",
    category: "fullstack",
    title: "LoveJesus AI",
    description:
      "A faith-based AI SaaS platform offering an AI Chat Assistant, Bible Q&A, Sermons & Prayers generation, Voice Input, Image & Video Generation, and Daily AI Quizzes. Built with a tiered subscription model (Free → GO → Plus → Pro Plus) featuring credit-based usage, document uploads, and saved documents — all tailored for the Christian community.",
    stack: [
      { name: "Next.js" },
      { name: "Tailwind CSS" },
      { name: "AI / LLM" },
      { name: "Stripe" },
      { name: "SaaS" },
    ],
    image: "/assets/workImages/lovejesusai.png",
    live: "https://lovejesusai.ai/",
    status: "Live",
  },
  {
    num: "06",
    category: "application",
    title: "Dukkanti",
    description:
      "Dukkanti is a niche e-commerce platform focused on nostalgic snacks and international grocery items.\nIt follows a mobile-first approach, with the website acting as a landing page to drive app downloads.\nThe platform emphasizes emotional buying through unique, hard-to-find food products and curated collections.",
    stack: [
      { name: "React Native" },
      { name: "Stripe Payment Gateway" },
      { name: "Firebase Auth" },
      { name: "Internationalization (AR/EN)" },
    ],
    image: "/assets/workImages/ss-4.webp",
    live: "https://play.google.com/store/apps/details?id=com.dukkanti.app&hl=en_IN",
    status: "Live",
  },
];

const statusStyles: Record<ProjectStatus, string> = {
  Live: "bg-green-500/20 text-green-400 border-green-500/30",
  Building: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "Coming Soon": "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

const StatusBadge = ({ status }: { status: ProjectStatus }) => {
  return (
    <span
      className={`px-3 py-1 text-xs font-semibold rounded-full border ${statusStyles[status]} cursor-target`}
    >
      {status}
    </span>
  );
};

const Work = () => {
  const [projectIndex, setProjectIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"slider" | "grid">("grid");
  const project = projects[projectIndex];
  const t = useTranslations("work");

  const handlePrev = () => setProjectIndex((i) => (i - 1 + projects.length) % projects.length);
  const handleNext = () => setProjectIndex((i) => (i + 1) % projects.length);

  return (
    <section className="min-h-[80vh] py-12 xl:py-0">
      <div className="container mx-auto">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="cursor-target">
            <h1 className="text-4xl font-bold text-white md:text-3xl">
              {t("title")}
            </h1>
            <p className="mt-2 max-w-2xl text-white/70">
              {t("description")}
            </p>
          </div>

          <div className="flex items-center gap-2 self-start rounded-xl border border-white/10 bg-white/5 p-1 cursor-target">
            <button
              onClick={() => setViewMode("grid")}
              className={`rounded-lg px-3 py-2 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                viewMode === "grid"
                  ? "bg-accent text-primary"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              } cursor-target`}
              aria-label={t("switchToGrid")}
              aria-pressed={viewMode === "grid"}
              title={t("gridTitle")}
            >
              <span className="flex items-center gap-2">
                <HiViewGrid className="text-xl" />
                <span className="text-sm font-medium">{t("grid")}</span>
              </span>
            </button>
            <button
              onClick={() => setViewMode("slider")}
              className={`rounded-lg px-3 py-2 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                viewMode === "slider"
                  ? "bg-accent text-primary"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              } cursor-target`}
              aria-label={t("switchToSlider")}
              aria-pressed={viewMode === "slider"}
              title={t("sliderTitle")}
            >
              <span className="flex items-center gap-2">
                <HiViewList className="text-xl" />
                <span className="text-sm font-medium">{t("slider")}</span>
              </span>
            </button>
          </div>
        </div>

        {viewMode === "slider" ? (
          <div className="flex flex-col xl:flex-row xl:gap-[30px]">
            <div className="w-full xl:w-[50%] xl:h-[460px] flex flex-col xl:justify-between order-2 xl:order-none">
              <div className="flex flex-col gap-[30px] h-[50%]">
                <div className="flex items-center gap-4 cursor-target">
                  <div className="text-4xl leading-none font-extrabold text-transparent text-outline cursor-target">
                    {project.num}
                  </div>
                  <StatusBadge status={project.status} />
                </div>
                <h2 className="text-[42px] font-bold leading-none text-white group-hover:text-accent transition-all duration-500 capitalize cursor-target">
                  {project.category} {t("projectLabel")}
                </h2>
                <h3 className="text-2xl font-semibold text-white cursor-target">
                  {project.title}
                </h3>
                <p className="max-w-2xl text-white/60 cursor-target">
                  {project.description}
                </p>
                <ul className="flex flex-wrap gap-2 cursor-target">
                  {project.stack.map((item) => (
                    <li
                      key={item.name}
                      className="px-3 py-1 text-sm text-accent bg-accent/10 border border-accent/20 rounded-md cursor-target"
                    >
                      {item.name}
                    </li>
                  ))}
                </ul>
                <div className="border border-white/20"></div>
                <div className="flex items-center gap-4">
                  {project.live && (
                    <Link
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View live project"
                    >
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger className="w-[60px] h-[60px] rounded-full bg-white/5 flex justify-center items-center group cursor-target hover:bg-white/10 hover:scale-110 hover:shadow-lg hover:shadow-accent/30 transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-105">
                            <BsArrowUpRight className="text-white text-3xl group-hover:text-accent cursor-target transition-all duration-500 group-hover:scale-110 group-hover:-rotate-12" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="cursor-target">{t("liveProject")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Link>
                  )}
                  {project.live && (
                    <Link
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-target rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      {t("viewProject")}
                    </Link>
                  )}
                </div>
              </div>
            </div>
            <div className="w-full xl:w-[50%] flex flex-col gap-4 cursor-target">
              <div className="relative h-[460px] w-full rounded-lg overflow-hidden cursor-target">
                <Image
                  key={project.image}
                  src={project.image}
                  fill
                  className="object-cover cursor-target"
                  alt={project.title}
                  priority
                  sizes="(max-width: 1280px) 100vw, 50vw"
                  quality={80}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 p-5 z-10">
                  <p className="text-sm font-medium uppercase tracking-wide text-white/80">
                    {project.category}
                  </p>
                  <h4 className="text-xl font-semibold text-white">
                    {project.title}
                  </h4>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">
                  {projectIndex + 1} / {projects.length}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrev}
                    className="bg-accent hover:bg-accent/80 text-primary w-[44px] h-[44px] flex justify-center items-center rounded transition-all cursor-target"
                    aria-label="Previous project"
                  >
                    <PiCaretLeftBold className="text-[22px]" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="bg-accent hover:bg-accent/80 text-primary w-[44px] h-[44px] flex justify-center items-center rounded transition-all cursor-target"
                    aria-label="Next project"
                  >
                    <PiCaretRightBold className="text-[22px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 cursor-target">
            {projects.map((currentProject) => (
              <div key={currentProject.title} className="group cursor-target">
                <Link
                  href={currentProject.live || "#"}
                  target={currentProject.live ? "_blank" : "_self"}
                  rel={currentProject.live ? "noopener noreferrer" : undefined}
                  className="block h-full cursor-target"
                >
                  <div className="bg-white/5 rounded-lg overflow-hidden border border-white/10 hover:border-accent/50 transition-all duration-300 hover:shadow-lg hover:shadow-accent/20 h-full flex flex-col cursor-target">
                    <div className="relative h-52 overflow-hidden cursor-target">
                      <Image
                        src={currentProject.image}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300 cursor-target"
                        alt={currentProject.title}
                        loading="lazy"
                        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                        quality={75}
                      />
                      <div className="absolute top-4 right-4 z-10 cursor-target">
                        <StatusBadge status={currentProject.status} />
                      </div>
                      <div className="absolute left-4 top-4 z-10 cursor-target">
                        <span className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs font-medium uppercase tracking-wide text-white">
                          {currentProject.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-grow cursor-target">
                      <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-accent transition-colors cursor-target">
                        {currentProject.title}
                      </h3>
                      <p className="text-white/60 text-sm mb-4 line-clamp-3 cursor-target">
                        {currentProject.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4 cursor-target">
                        {currentProject.stack.slice(0, 3).map((item) => (
                          <span
                            key={item.name}
                            className="px-2 py-1 text-xs text-accent bg-accent/10 rounded cursor-target"
                          >
                            {item.name}
                          </span>
                        ))}
                        {currentProject.stack.length > 3 && (
                          <span className="px-2 py-1 text-xs text-white/40 cursor-target">
                            +{currentProject.stack.length - 3}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-auto cursor-target">
                        <span className="text-sm text-accent font-medium cursor-target">
                          {t("viewLiveProject")}
                        </span>
                        <BsArrowUpRight className="text-accent group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform cursor-target" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Work;
